/**
 * The operator screen (V1-E06-T05): the human agents campaigns call on, and one of them at work.
 *
 *   the list    each operator's state: offline, ready, not ready, busy, wrap-up
 *   at work     who I am (remembered in this browser); ready or not ready; the contact a preview campaign
 *               offers me, with its details and how long is left; call it, or skip it (back for later, or
 *               not at all)
 *
 * The offer is polled every two seconds: an offer is short-lived and the API is the one source of truth.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { ApiError, data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Operator = Schemas["OperatorOut"];
type Preview = Schemas["PreviewOut"];
const ME_KEY = "swaralo.operator";
const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

function remembered(): string {
  try {
    return localStorage.getItem(ME_KEY) ?? "";
  } catch {
    return "";
  }
}

function remember(username: string) {
  try {
    localStorage.setItem(ME_KEY, username);
  } catch {
    // a private window: chosen again next time
  }
}

export function OperatorsPage() {
  const t = useT();
  const operators = useApiQuery("operators", (api) => api.GET("/v1/operators"), { refetchInterval: 5000 });
  const [me, setMe] = useState(remembered);
  return (
    <Page title={t("nav.operators")} description={t("operators.description")}>
      <Section query={operators}>
        {(list) =>
          list.length === 0 ? (
            <Empty title={t("operators.empty")}>
              <p>{t("operators.emptyBody")}</p>
            </Empty>
          ) : (
            <div className="space-y-6">
              <AtWork operators={list} me={me} choose={(u) => { setMe(u); remember(u); }} />
              <Table
                caption={t("operators.all")}
                rows={list}
                rowKey={(o) => o.username}
                columns={[
                  { header: t("operators.col.username"), cell: (o) => <span className="font-mono">{o.username}</span> },
                  { header: t("operators.col.name"), cell: (o) => o.display_name },
                  { header: t("operators.col.state"), cell: (o) => <State operator={o} /> },
                  { header: t("operators.col.devices"), cell: (o) => o.devices },
                ]}
              />
            </div>
          )
        }
      </Section>
    </Page>
  );
}

function State({ operator }: { operator: Operator }) {
  const t = useT();
  return <span>{t(`operators.state.${operator.state}` as MessageId)}</span>;
}

/** The operator I am: my state, and the contact offered to me. */
function AtWork({ operators, me, choose }: { operators: Operator[]; me: string; choose: (username: string) => void }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const canWork = useCan("campaigns:write");
  const canEnd = useCan("calls:write");
  const mine = operators.find((o) => o.username === me);
  const setState = useMutation({
    mutationFn: (state: "ready" | "not_ready") =>
      data(api.PUT("/v1/operators/{username}/state", { params: { path: { username: me } }, body: { state } })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["operators", tenant] }),
  });
  return (
    <section aria-labelledby={`${id}-title`} className="max-w-2xl space-y-3 rounded-md border border-[var(--color-border)] p-4">
      <h2 id={`${id}-title`} className="text-lg font-semibold">{t("operators.atWork")}</h2>
      <label htmlFor={`${id}-me`} className="flex flex-col gap-1 text-sm">
        {t("operators.me")}
        <select id={`${id}-me`} className={field} value={me} onChange={(e) => choose(e.target.value)}>
          <option value="">{t("operators.choose")}</option>
          {operators.map((o) => <option key={o.username} value={o.username}>{`${o.display_name || o.username} (${o.username})`}</option>)}
        </select>
      </label>
      {mine && (
        <>
          <p className="text-sm" aria-live="polite">{t("operators.now", { state: t(`operators.state.${mine.state}` as MessageId) })}</p>
          {mine.state === "offline" && <p className="text-sm text-[var(--color-muted)]">{t("operators.offlineHint")}</p>}
          {mine.line && <p className="text-sm">{t("operators.onLine")}</p>}
          {mine.line && mine.state === "busy" && mine.call_id && canEnd && <EndCall callId={mine.call_id} />}
          {canWork && (
            <div className="flex gap-2">
              <Button disabled={setState.isPending || mine.state === "ready"} onClick={() => setState.mutate("ready")}>
                {t("operators.ready")}
              </Button>
              <Button variant="secondary" disabled={setState.isPending || mine.state === "not_ready"}
                onClick={() => setState.mutate("not_ready")}>
                {t("operators.notReady")}
              </Button>
            </div>
          )}
          {setState.isError && <ErrorState error={setState.error} />}
          <Offer username={mine.username} canWork={canWork} />
        </>
      )}
    </section>
  );
}

/** A predictive campaign's contact with me on my line (V1-E06-T09): ended here, the line staying for the next. */
function EndCall({ callId }: { callId: string }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const end = useMutation({
    mutationFn: () => data(api.POST("/v1/calls/{call_id}/actions", {
      params: { path: { call_id: callId } }, body: { type: "hangup", params: { reason: "operator_ended" } },
    })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["operators", tenant] }),
  });
  return (
    <div className="space-y-1">
      <Button variant="secondary" disabled={end.isPending} onClick={() => end.mutate()}>{t("operators.endCall")}</Button>
      {end.isError && <ErrorState error={end.error} />}
    </div>
  );
}

/** The contact a preview campaign offers me now, if any: call it, or skip it. */
function Offer({ username, canWork }: { username: string; canWork: boolean }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const key = ["operator-preview", tenant, username];
  const offer = useQuery({
    queryKey: key,
    queryFn: async (): Promise<Preview | null> => {
      try {
        return await data(api.GET("/v1/operators/{username}/preview", { params: { path: { username } } }));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null; // nothing offered
        throw e;
      }
    },
    refetchInterval: 2000,
  });
  const decide = useMutation({
    mutationFn: async (choice: "accept" | "back" | "done") => {
      const callId = offer.data?.call_id ?? "";
      const path = { params: { path: { username } } };
      if (choice === "accept") {
        await data(api.POST("/v1/operators/{username}/preview/accept", { ...path, body: { call_id: callId } }));
      } else {
        await data(api.POST("/v1/operators/{username}/preview/skip", { ...path, body: { call_id: callId, contact: choice } }));
      }
    },
    onSettled: () => queries.invalidateQueries({ queryKey: key }),
  });
  if (offer.isError) return <ErrorState error={offer.error} />;
  if (!offer.data) return <p className="text-sm text-[var(--color-muted)]">{t("operators.noOffer")}</p>;
  const o = offer.data;
  // as of the last poll (every 2 s): what is left of the offer's time
  const left = Math.max(0, Math.round((new Date(o.expires_at).getTime() - offer.dataUpdatedAt) / 1000));
  return (
    <article aria-label={t("operators.offer")} className="space-y-3 rounded-md border border-[var(--color-primary)] p-3">
      <h3 className="font-semibold">{t("operators.offerFrom", { campaign: o.campaign_name })}</h3>
      <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-1 text-sm">
        <dt className="text-[var(--color-muted)]">{t("operators.number")}</dt><dd className="font-mono">{o.number}</dd>
        {Object.entries(o.variables).map(([k, v]) => (
          <div key={k} className="contents"><dt className="text-[var(--color-muted)]">{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>
      {o.state === "accepted" ? (
        <p className="text-sm" aria-live="polite">{t("operators.calling")}</p>
      ) : (
        <>
          <p className="text-sm" aria-live="polite">{t("operators.left", { seconds: left })}</p>
          {canWork && (
            <div className="flex flex-wrap gap-2">
              <Button disabled={decide.isPending} onClick={() => decide.mutate("accept")}>{t("operators.call")}</Button>
              <Button variant="secondary" disabled={decide.isPending} onClick={() => decide.mutate("back")}>
                {t("operators.later")}
              </Button>
              <Button variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate("done")}>
                {t("operators.never")}
              </Button>
            </div>
          )}
        </>
      )}
      {decide.isError && <ErrorState error={decide.error} />}
    </article>
  );
}
